import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { debounce } from '../../../../../util/fnc/debounce';
import { isNullOrEmptyArray } from '../../../../../util/arrays';
import {
  findCorrelationConfig,
  storeCorrelationConfig,
  updateCorrelationConfig,
  storeAutoCorrelationConfig,
} from '../../../../../stores/actions/configs';
import { Modal, InputText, QuestionIcon, Loading } from '../../../../../components';
import {
  SingleQuestionAwareCollapsibleList,
  MultiQuestionsAwareCollapsibleList,
} from '../../../../AwareCollapsibleList';
import Scores from '../../../../Scores';
import { usePromptConfirm, useErrorToast } from '../../../../../hooks';
import * as Ui from './styles';


const msgSaveWithSuccess = 'Configuração salva com sucesso';
const stages = {
  WAITING: 0,
  EMPTY: 1,
};

// TODO: temporário para resolver a configuração local
function getAutoCorrelationConfig(customerId, surveyId) {
  const key = `${customerId}.${surveyId}`;
  const value = localStorage.getItem(key);
  if (value) {
    return parseFloat(value);
  }
  return 0.1;
}

const titleLength = 100;

function CorrelationConfig({
  // identidade do cliente
  customerId,
  // identidade da pesquisa
  surveyId,
  // identidade da pergunta principal
  questionId,
  // enunciado da pergunta principal
  questionTitle,
  // identificador da configuração (usado para edição)
  configId,
  // define se o modal está aberto
  isOpen = false,
  // propaga o evento de acionamento de fechar o modal
  onClose,
  // propaga o evento de acionamento de salvar os dados
  onSave,
  // outros propriedades
  ...props
}) {
  // props from redux
  const { dispatch, running, error, corrConfigData } = props;

  // prompts
  const [openConfirm, renderConfirm] = usePromptConfirm();

  // state
  const [stage, setStage] = useState(stages.EMPTY);
  const [isShowing, show] = useState(isOpen);
  const [title, setTitle] = useState('');
  const [mainAlternative, setMainAlternative] = useState({ questionId, answerId: null });
  const [corrQuestionsIds, setCorrQuestionsIds] = useState([/* numbers */]);
  const [minMaxCorrValue, setMinMaxCorrValue] = useState(0.1);
  const [hiddenOkButton, sethiddenOkButton] = useState(true);
  const [selectedContentIndex, setSelectedContentIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);

  //
  const [checkError] = useErrorToast();

  // refs: modal component
  const modalRef = useRef(null);
  const singleCollapsedListRef = useRef(null);
  const multiCollapsedListRef = useRef(null);
  const debouncedSetTile = debounce((newTitle) => setTitle(newTitle), 500);

  /**
   * callbacks: reinicia todos os dados
   */
  const resetAll = useCallback(() => {
    setTitle('');
    show(false);
    setMinMaxCorrValue(0.1);
    setMainAlternative({ questionId, answerId: null });
    setCorrQuestionsIds([]);
    setSelectedContentIndex(0);
    setStage(stages.EMPTY);
    if (modalRef.current) {
      modalRef.current.selectTab(0);
    }
    if (singleCollapsedListRef.current) {
      singleCollapsedListRef.current.reset();
    }
    if (multiCollapsedListRef.current) {
      multiCollapsedListRef.current.reset();
    }
  }, [questionId]);

  /**
   * callbacks: fecha esta configuração
   */
  const close = useCallback((saving, editing) => {
    resetAll();
    // propagar evento de salvar e fechar
    if (saving && onSave) {
      onSave(editing);
    }
    //
    if (!saving && onClose) {
      onClose();
    }
  }, [resetAll, onSave, onClose]);

  // efeito de alterar a pergunta
  useEffect(() => {
    if (questionId) sethiddenOkButton(false);
  }, [questionId]);

  // initialize tab (auto)
  useEffect(() => {
    if (selectedContentIndex === 1) {
      setMinMaxCorrValue(getAutoCorrelationConfig(
        customerId, surveyId,
      ));
    }
  }, [customerId, selectedContentIndex, surveyId]);

  // on initialize in edit mode
  useEffect(() => {
    if (corrConfigData && editMode && !title) {
      // title
      setTitle(corrConfigData.titulo);
      // score
      setMinMaxCorrValue(corrConfigData.nota_corta);
      // questions
      setCorrQuestionsIds(corrConfigData.perguntas_relacionadas);
      // alternatives
      const { idPergunta, idAlternativa } = corrConfigData.resposta_correlacionada;
      setMainAlternative({ questionId: idPergunta, answerId: idAlternativa });
    }
  }, [editMode, corrConfigData, title]);

  /**
   * Dispara uma chamada para recuperar os dados para entrar no modo de edição
   */
  useEffect(() => {
    if (configId) {
      findCorrelationConfig(customerId, surveyId, questionId, configId, dispatch);
      setEditMode(true);
    }
  }, [dispatch, configId, customerId, questionId, surveyId]);

  /**
   * É responsabilidade deste componente controlar a abertura e fechamento
   * do Modal, logo, é melhor controlar essa abertura através do estado
   * ao invés de delegar para o "parent" através de isOpen.
   *
   * Dessa forma foi criado um isShowing, cujo efeito de alteração em isOpen
   * pode ser percebido por este componente. Ficando, dessa forma, o controle
   * de abertura e fechamento sob responsabilidade deste componente.
   *
   * Seguir este raciocínio é recomendado.
   */
  useEffect(() => {
    show(isOpen);
  }, [isOpen]);

  /**
   * No caso de haver erro depois que solicitar salvar
   */
  useEffect(() => {
    if (stage === stages.WAITING && !running) {
      toast.success(msgSaveWithSuccess);
      close(true, editMode);
    }
  }, [close, editMode, running, stage]);

  // error effect
  useEffect(() => {
    if (error) {
      checkError(error);
    }
  }, [checkError, error]);

  function handleManualConfigSave() {
    if (!title) {
      toast.error('Defina um título');
      return;
    }

    // validar dados
    if (!mainAlternative.answerId) {
      toast.error('Selecione pelo menos uma resposta da pergunta principal');
      return;
    }

    if (isNullOrEmptyArray(corrQuestionsIds)) {
      toast.error('Selecione pelo menos uma pergunta para correlacionar');
      return;
    }

    // open dialog to storing
    openConfirm(() => {
      // dados da configuração
      const configData = {
        customerId,
        surveyId,
        title,
        mainAnswer: mainAlternative,
        questionsIds: corrQuestionsIds,
        minMaxCorrValue,
      };
      // ação de salvar ou alterar
      if (editMode) {
        updateCorrelationConfig(configId, configData, dispatch);
      } else {
        storeCorrelationConfig(configData, dispatch);
      }
      //
      setStage(stages.WAITING);
    });
  }

  function handleAutoConfigSave() {
    // open dialog to storing
    openConfirm(() => {
      // dados da configuração
      const configData = {
        customerId,
        surveyId,
        minMaxCorrValue,
      };
      // ação de salvar
      storeAutoCorrelationConfig(configData, dispatch);
      // propagar evento de salvamento
      setStage(stages.WAITING);
    });
  }

  /**
   * save config
   */
  function handleSave() {
    if (selectedContentIndex === 1) {
      handleAutoConfigSave();
    } else {
      handleManualConfigSave();
    }
  }

  function handleCancel() {
    close();
  }

  function handleChangeTitle(value) {
    if (value) {
      debouncedSetTile(value);
    }
  }

  function handleChangeMainAlternatives(itemsIds) {
    if (!isNullOrEmptyArray(itemsIds)) {
      setMainAlternative({ questionId, answerId: itemsIds[0] });
    } else {
      setMainAlternative({ questionId, answerId: null });
    }
  }

  function handleChangeSelectedQuestions(questionsIds) {
    setCorrQuestionsIds(questionsIds);
  }

  function handleMinMaxValue(items) {
    if (!isNullOrEmptyArray(items)) {
      const { value } = items[0];
      setMinMaxCorrValue(value);
    }
  }

  function handleSelectTab(index, reverseCallback) {
    if (!customerId || !surveyId || !questionId) {
      reverseCallback(true);
    } else {
      setSelectedContentIndex(index);
    }
  }

  // renderizar uma mensagem de erro
  function renderError() {
    return (
      <span className="errorMessage">
        Para realizar uma configuração é necessário selecionar a pergunta principal
      </span>
    );
  }

  function renderContent() {
    return (
      <Ui.Container>
        {/* tab#0 */}
        <Ui.TabContainer className={selectedContentIndex === 0 ? 'tab-visible' : 'tab-gone'}>
          <Ui.TitleGroup>
            <Ui.Label>{'Título da Configuração'}</Ui.Label>
            <InputText onChange={handleChangeTitle} value={title} maxLength={titleLength} />
          </Ui.TitleGroup>
          <Ui.ItemsGroup>
            {/*  */}
            <Ui.FixedItem>
              <Ui.Label>
                {'Pergunta principal'}
                <Ui.LabelSmall>
                  {'(selecione a alternativa a ser correlacionada)'}
                </Ui.LabelSmall>
              </Ui.Label>
              <SingleQuestionAwareCollapsibleList
                checkedTitle
                boldedTitle
                expansible
                selectableItem
                customerId={customerId}
                surveyId={surveyId}
                questionId={questionId}
                questionTitle={questionTitle}
                selectedItems={[mainAlternative.answerId]}
                onChangeSelectedAlternativesIds={(qId, aIds) => handleChangeMainAlternatives(aIds)}
              />
            </Ui.FixedItem>
            <Ui.DynamicItems>
              <MultiQuestionsAwareCollapsibleList
                showScore
                expansibleItems
                // ref={selectorRef}
                customerId={customerId}
                surveyId={surveyId}
                initialScore={minMaxCorrValue}
                label={'Selecione as perguntas para relacionar a alternativa'}
                onSelectQuestions={handleChangeSelectedQuestions}
                onChangeMinMaxRange={handleMinMaxValue}
                questionsToIgnore={[questionId]}
                initialSelectedQuestionsIds={corrQuestionsIds}
              />
            </Ui.DynamicItems>
          </Ui.ItemsGroup>
        </Ui.TabContainer>
        {/* tab#1 */}
        <Ui.TabContainer className={selectedContentIndex === 1 ? 'tab-visible' : 'tab-gone'}>
          <Ui.ItemsGroup>
            {/*  */}
            <Ui.FixedItem>
              <Ui.Label>
                {'Pergunta principal'}
                <Ui.LabelSmall>
                  {'(automaticamente todas as alternativas são selecionadas)'}
                </Ui.LabelSmall>
              </Ui.Label>
              <SingleQuestionAwareCollapsibleList
                checkedTitle
                boldedTitle
                expansible
                customerId={customerId}
                surveyId={surveyId}
                questionId={questionId}
                questionTitle={questionTitle}
              />
            </Ui.FixedItem>
            <Ui.ScoreZone>
              <Scores
                dark
                initialScore={minMaxCorrValue}
                onChange={handleMinMaxValue}
              />
            </Ui.ScoreZone>
          </Ui.ItemsGroup>
        </Ui.TabContainer>
      </Ui.Container>
    );
  }

  function renderModalBody() {
    if (running) {
      return (<Loading size={30} running />);
    }
    if (customerId && surveyId && questionId) {
      return renderContent();
    }
    return renderError();
  }

  return (
    <Ui.Wrapper>
      <Modal
        ref={modalRef}
        isLoading={running}
        isOpen={isShowing}
        title={'Configuração de Correlação'}
        okBtnTitle={'Salvar'}
        cancelBtnTitle={'Cancelar'}
        onOk={handleSave}
        onCancel={handleCancel}
        hiddenOkButton={hiddenOkButton}
        tabs={['Correlação manual', 'Correlação automático']}
        onSelectTab={(index, reverse) => handleSelectTab(index, reverse)}
      >
        {renderModalBody()}
        {renderConfirm(QuestionIcon, 'Deseja salvar esta configuração?')}
      </Modal>
    </Ui.Wrapper>
  );
}

const mapStateToProps = (state) => {
  const configsState = state.configs.correlationConfig;
  const autoConfigsState = state.configs.autoCorrelationConfig;
  return {
    running: configsState.running || autoConfigsState.running,
    error: (configsState.error && configsState.error) || (autoConfigsState.error && autoConfigsState.error),
    corrConfigData: configsState.payload ? configsState.payload : null,
    autoMinMaxCorrValue: autoConfigsState.payload,
  };
};

export default connect(mapStateToProps)(CorrelationConfig);
